import React from 'react'
import clsx from 'clsx'
import { Eye, EyeOff, Check, AlertCircle, Loader2 } from 'lucide-react'

type ValidationState = 'none' | 'checking' | 'valid' | 'invalid'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  success?: string
  helpText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  containerClassName?: string
  validation?: ValidationState
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    type = 'text', 
    label, 
    error, 
    success,
    helpText,
    leftIcon, 
    rightIcon, 
    containerClassName,
    validation = 'none',
    id,
    disabled,
    ...props 
  }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const inputId = id || React.useId()
    const isPassword = type === 'password'
    const currentType = isPassword && showPassword ? 'text' : type

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword)
    }

    // Determine validation icon
    const getValidationIcon = () => {
      switch (validation) {
        case 'checking':
          return <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />
        case 'valid':
          return <Check className="h-4 w-4 text-green-400" />
        case 'invalid':
          return <AlertCircle className="h-4 w-4 text-red-400" />
        default:
          return null
      }
    }

    const hasValidationIcon = validation !== 'none'
    const hasRightContent = rightIcon || isPassword || hasValidationIcon

    return (
      <div className={clsx('space-y-2', containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-white"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <div className="h-4 w-4 text-slate-400">{leftIcon}</div>
            </div>
          )}
          <input
            type={currentType}
            className={clsx(
              'input w-full bg-slate-800 border border-slate-600 text-white placeholder-slate-400 rounded-md px-3 py-2 text-sm transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
              leftIcon && 'pl-10',
              hasRightContent && 'pr-10',
              error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
              success && 'border-green-500 focus:border-green-500 focus:ring-green-500',
              disabled && 'cursor-not-allowed opacity-50',
              className
            )}
            ref={ref}
            id={inputId}
            disabled={disabled}
            {...props}
          />
          
          {/* Right side content */}
          {isPassword && (
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white transition-colors"
              onClick={togglePasswordVisibility}
              disabled={disabled}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}
          {!isPassword && hasValidationIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {getValidationIcon()}
            </div>
          )}
          {!isPassword && !hasValidationIcon && rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <div className="h-4 w-4 text-slate-400">{rightIcon}</div>
            </div>
          )}
        </div>
        
        {/* Helper text */}
        {(error || success || helpText) && (
          <div className="text-sm animate-slide-up">
            {error && (
              <p className="text-red-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
            {!error && success && (
              <p className="text-green-400 flex items-center gap-1">
                <Check className="h-3 w-3" />
                {success}
              </p>
            )}
            {!error && !success && helpText && (
              <p className="text-slate-400">{helpText}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
